import { Dispatch } from 'redux';
import up = require('updeep');
import * as selectors from '../selectors';
import { State } from '../state';
import { ImageResponse, ImageMutation } from '../../userApi';
import { ExtraArguments } from '../extraArguments';
import { postFormData } from '../../utils/userApiHelper';
import { patch } from './patch';
import {
  dataURLToBlob, DataURLFromFile, setDataURLDimensions, getExtensionForMimeType, blobToDataURL,
  prepareImageForMutation, ErrCouldNotCompressFile
} from '../../utils/image';
import { maxImageSizeBytes } from '../../utils/userApiHelper';
import { toast } from './toast';
import { StyleState } from '../../utils/styles';
import { handleUnexpectedError } from './handleUnexpectedError';
import { trackEvent } from '../../utils/googleAnalytics';

export const clearPostedImage = (inputID: string) => {
  return patch({ postedImages: up.omitBy((_value: string, k: string) => k === inputID) });
};

// If postImage does not return an ImageResponse, but getImage(imageID) exists,
// you can assume that saving the image failed.
export const postImage = (imageID: string) =>
  async (dispatch: Dispatch, getState: () => State, { i18n, getImage, delImage }: ExtraArguments): Promise<ImageResponse> => {
    const t = i18n.getFixedT(i18n.language, 'image');
    const dataURLFromFile = getImage(imageID);
    if (!dataURLFromFile) return;

    const formData = new FormData();
    if (dataURLFromFile.file) {
      formData.append('image', dataURLFromFile.file, dataURLFromFile.filename);
    } else {
      const blob = await dataURLToBlob(dataURLFromFile);
      formData.append('image', blob, dataURLFromFile.filename);
    }
    const state = getState();
    const basePath = selectors.userApiBasePath(state);
    try {
      const imgResponse = await postFormData<ImageResponse>(`${basePath}/image`, formData, state.token);
      trackEvent('image', 'create');
      delImage(imageID);
      dispatch(patch({
        postedImages: { [imageID]: imgResponse.url }
      }));
      return imgResponse;
    } catch (err) {
      handleUnexpectedError(err, {
        message: t('We failed to save your photo or video. ') + (err.message || '')
      })(dispatch, getState);
      trackEvent('image', 'unhandledError', 'create');
    }
  };

export const postImageMutation = (dataURLFromFile: DataURLFromFile, mutation: ImageMutation) =>
async (dispatch: Dispatch, getState: () => State, extra: ExtraArguments) => {
  const { i18n } = extra;
  const t = i18n.getFixedT(i18n.language, 'image');
  dispatch(patch({ spinner: true }));
  try {
    const state = getState();
    const { token } = state;
    let uploadSource = dataURLFromFile;
    try {
      // Compress to ~1MB JPEG client-side first. Phone screenshots/camera photos
      // were failing when we sent full-size files and asked the API for PNG ≤1MB.
      uploadSource = await prepareImageForMutation(dataURLFromFile, maxImageSizeBytes);
    } catch (compressErr) {
      if (compressErr === ErrCouldNotCompressFile) {
        throw new Error(t('This photo is too large to upload. Try a smaller image.'));
      }
      throw compressErr;
    }
    // Prefer JPEG for mutations — PNG recompress of photos often exceeds the 1MB target.
    const mutationForUpload = {
      ...mutation,
      contentType: uploadSource.mimeType === 'image/jpeg'
        ? 'image/jpeg'
        : (mutation.contentType || uploadSource.mimeType),
      maxMB: mutation.maxMB && mutation.maxMB > 0 ? Math.max(mutation.maxMB, 1) : 1
    };
    const blob = await dataURLToBlob(uploadSource);
    const formData = new FormData();
    formData.append('image', blob, uploadSource.filename);
    formData.append('mutation', JSON.stringify(mutationForUpload));

    const options = {
      body: formData,
      headers: { Authorization: token, Accept: mutationForUpload.contentType || uploadSource.mimeType },
      method: 'POST'
    };
    const basePath = selectors.userApiBasePath(state);

    const response = await fetch(`${basePath}/image_mutation`, options);
    trackEvent('image', 'mutate');
    if (!response.ok) {
      if (response.status === 413) {
        throw new Error(t('This photo is too large to upload. Try a smaller image.'));
      }
      throw new Error(t('We had trouble editing your photo. Try cropping this photo or using another.'));
    }
    const mimeType = response.headers.get('Content-Type') || '';
    if (!mimeType.startsWith('image/')) {
      throw new Error(t('We had trouble editing your photo. Try cropping this photo or using another.'));
    }
    const blob2 = await response.blob();
    const dataURL = await blobToDataURL(blob2);
    const newDataURL = await setDataURLDimensions({
      filename: uploadSource.filename.replace(/\.\w+$/, getExtensionForMimeType(mimeType)),
      width: 0, height: 0,
      dataURL,
      mimeType
    });
    dispatch(patch({ spinner: false }));
    return newDataURL;
  } catch (err) {
    dispatch(patch({ spinner: false }));
    const message = (err && err.message) || t('We had trouble editing your photo. Try cropping this photo or using another.');
    toast({
      style: StyleState.Warning,
      message
    })(dispatch, getState);
    if ((err && err.message) && err.message.indexOf('too large') >= 0) {
      trackEvent('image', 'unhandledError', 'mutate_too_large');
      return;
    }
    handleUnexpectedError(err)(dispatch, getState);
    trackEvent('image', 'unhandledError', 'mutate');
  }
};
